import React, { useState, useRef } from "react";

// ─── Constantes ───────────────────────────────────────────────────────────────

const SECTIONS_MODEL = [
    {
        id: 1,
        key: "objetivo",
        label: "Objetivo del Desarrollo",
        desc: "Propósito, alcance y justificación del requerimiento.",
    },
    {
        id: 2,
        key: "solicitante",
        label: "Datos del Solicitante",
        desc: "Área, responsable, fecha y número de requerimiento.",
    },
    {
        id: 3,
        key: "alcance",
        label: "Alcance Funcional",
        desc: "Procesos cubiertos, exclusiones y dependencias.",
    },
    {
        id: 4,
        key: "reglasNegocio",
        label: "Reglas de Negocio",
        desc: "Condiciones, validaciones y lógica de procesamiento.",
    },
    {
        id: 5,
        key: "flujo",
        label: "Flujo del Proceso",
        desc: "Diagrama o descripción paso a paso del proceso.",
    },
    {
        id: 6,
        key: "interfaces",
        label: "Interfaces y Formularios",
        desc: "Pantallas, campos, botones y comportamiento de UI.",
    },
    {
        id: 7,
        key: "datos",
        label: "Modelo de Datos / Tablas SAP",
        desc: "Tablas, campos, estructuras y diccionario de datos.",
    },
    {
        id: 8,
        key: "reportes",
        label: "Reportes y Salidas",
        desc: "Layouts de reportes, archivos de salida o formularios.",
    },
    {
        id: 9,
        key: "seguridad",
        label: "Seguridad y Autorizaciones",
        desc: "Roles, perfiles, objetos de autorización requeridos.",
    },
    {
        id: 10,
        key: "criterios",
        label: "Criterios de Aceptación",
        desc: "Casos de prueba, condiciones de aprobación y UAT.",
    },
];

const STATUS = { complete: "complete", partial: "partial", missing: "missing" };

const STATUS_CONFIG = {
    complete: {
        icon: "✅",
        label: "Completa",
        color: "text-emerald-600",
        bg: "bg-emerald-50",
        border: "border-emerald-200",
    },
    partial: {
        icon: "⚠️",
        label: "Incompleta",
        color: "text-amber-600",
        bg: "bg-amber-50",
        border: "border-amber-200",
    },
    missing: {
        icon: "❌",
        label: "Faltante",
        color: "text-red-500",
        bg: "bg-red-50",
        border: "border-red-200",
    },
};

// ─── Helper: leer archivo como base64 ────────────────────────────────────────

const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = () => reject(new Error("Error al leer el archivo"));
        reader.readAsDataURL(file);
    });

// ─── Prompt del sistema para Claude ──────────────────────────────────────────

const buildSystemPrompt = () =>
    `
Eres un arquitecto SAP senior especializado en auditoría de documentos funcionales.
Tu tarea es analizar el documento adjunto y evaluar la presencia y calidad de 10 secciones estándar.

Para cada sección devuelve un JSON con exactamente esta estructura (sin markdown, sin texto extra):
{
  "sections": [
    {
      "key": "<key de la sección>",
      "status": "complete" | "partial" | "missing",
      "score": <número 0-10>,
      "findings": "<observación concisa de 1-2 oraciones en español>",
      "suggestions": "<recomendación concisa en español, o null si status es complete>"
    }
  ],
  "globalSummary": "<resumen ejecutivo de 2-3 oraciones sobre la calidad general del documento>"
}

Las 10 secciones a evaluar son:
1. objetivo        - Objetivo del Desarrollo
2. solicitante     - Datos del Solicitante
3. alcance         - Alcance Funcional
4. reglasNegocio   - Reglas de Negocio
5. flujo           - Flujo del Proceso
6. interfaces      - Interfaces y Formularios
7. datos           - Modelo de Datos / Tablas SAP
8. reportes        - Reportes y Salidas
9. seguridad       - Seguridad y Autorizaciones
10. criterios      - Criterios de Aceptación

Criterios de status:
- "complete": La sección está presente y tiene información suficiente y clara (score 8-10)
- "partial":  La sección está presente pero le falta detalle o claridad (score 3-7)
- "missing":  La sección no existe o tiene menos de 2 líneas sin valor (score 0-2)

Sé riguroso pero justo. Responde SOLO con el JSON, sin bloques de código.
`.trim();

// ─── Componente principal ─────────────────────────────────────────────────────

const SpecValidatorWizard = ({ data, setData, apiUrl, getToken }) => {
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [fixedDoc, setFixedDoc] = useState(null);
    const [downloading, setDownloading] = useState('');
    const [fileText, setFileText] = useState("");
    const inputRef = useRef();

    // ── Manejo de archivo ──────────────────────────────────────────────────────

    const acceptFile = (f) => {
        const allowed = [
            "application/pdf",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "application/msword",
            "text/plain",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "application/vnd.ms-excel",
        ];
        const ext = f.name.split(".").pop().toLowerCase();
        const allowedExt = ["pdf", "doc", "docx", "txt", "xlsx", "xls"];

        if (!allowed.includes(f.type) && !allowedExt.includes(ext)) {
            setError("Solo se aceptan archivos PDF, Word, Excel o TXT");
            return;
        }
        if (f.size > 10 * 1024 * 1024) {
            setError("El archivo no puede superar los 10 MB");
            return;
        }
        setFile(f);
        setError("");
        setResult(null);
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        if (e.dataTransfer.files[0]) acceptFile(e.dataTransfer.files[0]);
    };

    // ── Llamada a la API ───────────────────────────────────────────────────────

    const handleAnalyze = async () => {
        if (!file) return;
        setLoading(true);
        setError("");
        setResult(null);

        try {
            const isPdf = file.type === "application/pdf";
            let fileBase64;

            if (isPdf) {
                fileBase64 = await readFileAsBase64(file);
            } else {
                fileBase64 = await new Promise((res, rej) => {
                    const reader = new FileReader();
                    reader.onload = (e) => res(e.target.result);
                    reader.onerror = () => rej(new Error("Error al leer archivo"));
                    reader.readAsText(file, "utf-8");
                });
            }

            // Guardar texto completo para posible generación de documento corregido
            if (!isPdf) setFileText(fileBase64);

            const token = await getToken();

            const response = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    toolId: "specValidator",
                    fileBase64,
                    fileType: file.type,
                    fileName: file.name,
                }),
            });

            if (!response.ok) throw new Error(`Error: ${response.status}`);

            const apiData = await response.json();
            const clean = apiData.result.replace(/```json|```/g, "").trim();
            const parsed = JSON.parse(clean);

            setResult(parsed);
            setData({ ...data, validationResult: parsed, fileName: file.name });
        } catch (err) {
            setError(`Error al analizar el documento: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // ── Generar documento corregido ─────────────────────────────────────────
    const handleGenerateFix = async () => {
        if (!result) return;
        setGenerating(true);
        setFixedDoc(null);
        try {
            const token = await getToken();
            const response = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    toolId: "specValidatorFix",
                    validationResult: result,
                    originalText: fileText,
                }),
            });
            if (!response.ok) throw new Error(`Error: ${response.status}`);
            const apiData = await response.json();
            setFixedDoc(apiData.result);
        } catch (err) {
            setError(`Error al generar documento: ${err.message}`);
        } finally {
            setGenerating(false);
        }
    };
    // ── Cálculos derivados ─────────────────────────────────────────────────────

    const score = result
        ? Math.round(
            (result.sections.reduce((acc, s) => acc + s.score, 0) /
                (SECTIONS_MODEL.length * 10)) *
            100,
        )
        : null;

    const countBy = (st) =>
        result?.sections.filter((s) => s.status === st).length ?? 0;

    const scoreColor =
        score === null
            ? ""
            : score >= 80
                ? "text-emerald-600"
                : score >= 50
                    ? "text-amber-500"
                    : "text-red-500";

    const scoreRing =
        score === null
            ? "border-slate-200"
            : score >= 80
                ? "border-emerald-400"
                : score >= 50
                    ? "border-amber-400"
                    : "border-red-400";

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 animate-in slide-in-from-left duration-500 text-left">
            {/* ── Columna izquierda: Upload + acción ── */}
            <div className="space-y-6">
                {/* Drop zone */}
                <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block italic">
                        Documento Funcional (PDF o Word)
                    </label>

                    <div
                        onDragOver={(e) => {
                            e.preventDefault();
                            setDragOver(true);
                        }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={onDrop}
                        onClick={() => inputRef.current.click()}
                        className={`
              relative w-full min-h-[160px] rounded-2xl border-2 border-dashed cursor-pointer
              flex flex-col items-center justify-center gap-3 transition-all duration-200
              ${dragOver
                                ? "border-blue-400 bg-blue-50 scale-[1.01]"
                                : file
                                    ? "border-emerald-400 bg-emerald-50"
                                    : "border-slate-200 bg-slate-50 hover:border-blue-300 hover:bg-blue-50/40"
                            }
            `}
                    >
                        <input
                            ref={inputRef}
                            type="file"
                            accept=".pdf,.doc,.docx,.txt,.xlsx,.xls"
                            className="hidden"
                            onChange={(e) =>
                                e.target.files[0] && acceptFile(e.target.files[0])
                            }
                        />

                        {file ? (
                            <>
                                <div className="text-3xl">📄</div>
                                <div className="text-center px-4">
                                    <p className="font-black text-sm text-emerald-700 break-all">
                                        {file.name}
                                    </p>
                                    <p className="text-[10px] text-emerald-500 mt-1">
                                        {(file.size / 1024).toFixed(1)} KB — listo para analizar
                                    </p>
                                </div>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setFile(null);
                                        setResult(null);
                                    }}
                                    className="text-[10px] text-slate-400 hover:text-red-400 underline transition-colors"
                                >
                                    Cambiar archivo
                                </button>
                            </>
                        ) : (
                            <>
                                <div className="text-4xl opacity-40">📂</div>
                                <div className="text-center px-6">
                                    <p className="font-black text-sm text-slate-500">
                                        Arrastrá el archivo aquí
                                    </p>
                                    <p className="text-[10px] text-slate-400 mt-1">
                                        o hacé clic para seleccionar
                                    </p>
                                    <p className="text-[10px] text-slate-300 mt-2">
                                        PDF · DOCX · DOC — máx. 10 MB
                                    </p>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Error */}
                {error && (
                    <div className="p-3 bg-red-50 border-2 border-red-100 rounded-xl">
                        <p className="text-[11px] font-bold text-red-500">{error}</p>
                    </div>
                )}

                {/* Botón analizar */}
                <button
                    onClick={handleAnalyze}
                    disabled={!file || loading}
                    className={`
            w-full py-4 rounded-2xl font-black text-sm uppercase tracking-widest transition-all duration-200
            ${!file || loading
                            ? "bg-slate-100 text-slate-300 cursor-not-allowed"
                            : "bg-blue-600 text-white shadow-lg shadow-blue-200 hover:bg-blue-700 hover:shadow-blue-300 hover:scale-[1.01] active:scale-[0.99]"
                        }
          `}
                >
                    {loading ? (
                        <span className="flex items-center justify-center gap-2">
                            <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Analizando documento…
                        </span>
                    ) : (
                        "🔍 Validar Especificación"
                    )}
                </button>

                {/* Modelo de referencia */}
                <div className="bg-slate-50 border-2 border-slate-100 rounded-2xl p-4">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 italic">
                        Modelo estándar — 10 secciones
                    </p>
                    <ul className="space-y-1.5">
                        {SECTIONS_MODEL.map((s) => (
                            <li key={s.key} className="flex items-start gap-2">
                                <span className="text-[10px] font-black text-slate-300 mt-0.5 w-4 shrink-0">
                                    {s.id}.
                                </span>
                                <div>
                                    <p className="text-[11px] font-black text-slate-600">
                                        {s.label}
                                    </p>
                                    <p className="text-[10px] text-slate-400">{s.desc}</p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* ── Columna derecha: Resultado ── */}
            <div className="space-y-4">
                {!result && !loading && (
                    <div className="flex flex-col items-center justify-center h-full min-h-[300px] text-center px-6 bg-slate-50 rounded-[32px] border-2 border-dashed border-slate-100">
                        <div className="text-5xl mb-4 opacity-20">📋</div>
                        <p className="font-black text-slate-300 text-sm uppercase tracking-wider">
                            El reporte aparecerá aquí
                        </p>
                        <p className="text-[10px] text-slate-300 mt-1">
                            Subí un documento y hacé clic en Validar
                        </p>
                    </div>
                )}

                {loading && (
                    <div className="flex flex-col items-center justify-center h-full min-h-[300px] gap-4 bg-blue-50/40 rounded-[32px] border-2 border-blue-100/50">
                        <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                        <div className="text-center">
                            <p className="font-black text-blue-700 text-sm">
                                Analizando con Claude…
                            </p>
                            <p className="text-[10px] text-blue-400 mt-1">
                                Evaluando las 10 secciones estándar
                            </p>
                        </div>
                    </div>
                )}

                {result && (
                    <>
                        {/* Score card */}
                        <div className="bg-white border-2 border-slate-100 rounded-[28px] p-5 flex items-center gap-5 shadow-sm">
                            <div
                                className={`w-20 h-20 shrink-0 rounded-full border-4 ${scoreRing} flex flex-col items-center justify-center shadow-inner`}
                            >
                                <span className={`text-2xl font-black ${scoreColor}`}>
                                    {score}%
                                </span>
                                <span className="text-[8px] text-slate-400 font-bold uppercase tracking-wider">
                                    Score
                                </span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-black text-slate-700 text-sm mb-2">
                                    Completitud del Documento
                                </p>
                                <div className="flex gap-3 flex-wrap">
                                    {[STATUS.complete, STATUS.partial, STATUS.missing].map(
                                        (st) => (
                                            <span
                                                key={st}
                                                className={`text-[10px] font-black px-2 py-1 rounded-lg ${STATUS_CONFIG[st].bg} ${STATUS_CONFIG[st].color}`}
                                            >
                                                {STATUS_CONFIG[st].icon} {countBy(st)}{" "}
                                                {STATUS_CONFIG[st].label}
                                                {countBy(st) !== 1 ? "s" : ""}
                                            </span>
                                        ),
                                    )}
                                </div>
                                {result.globalSummary && (
                                    <p className="text-[10px] text-slate-500 mt-2 leading-relaxed line-clamp-3">
                                        {result.globalSummary}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Barra de progreso */}
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div
                                className={`h-full rounded-full transition-all duration-700 ${score >= 80
                                    ? "bg-emerald-400"
                                    : score >= 50
                                        ? "bg-amber-400"
                                        : "bg-red-400"
                                    }`}
                                style={{ width: `${score}%` }}
                            />
                        </div>
                        {/* Botón generar documento corregido */}
                        <button
                            onClick={handleGenerateFix}
                            disabled={generating}
                            className={`w-full py-3 rounded-2xl font-black text-sm uppercase tracking-widest transition-all duration-200 ${generating
                                ? "bg-slate-100 text-slate-300 cursor-not-allowed"
                                : "bg-emerald-600 text-white shadow-lg shadow-emerald-200 hover:bg-emerald-700 hover:scale-[1.01]"
                                }`} >
                            {generating ? (
                                <span className="flex items-center justify-center gap-2">
                                    <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    Generando documento corregido…
                                </span>
                            ) : (
                                "✨ Generar Documento Funcional Corregido"
                            )}
                        </button>

                        {/* Generando documento corregido */}
                        {generating && (
                            <div className="flex flex-col items-center justify-center gap-4 bg-emerald-50/40 rounded-[32px] border-2 border-emerald-100/50 p-10">
                                <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
                                <div className="text-center">
                                    <p className="font-black text-emerald-700 text-sm uppercase tracking-widest">
                                        Generando documento corregido...
                                    </p>
                                    <p className="text-[10px] text-emerald-500 mt-1">
                                        Claude está redactando las 10 secciones completas
                                    </p>
                                    <div className="flex justify-center gap-1 mt-3">
                                        <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                        <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                        <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Documento corregido */}
                        {fixedDoc && (
                            <div className="bg-slate-50 border-2 border-emerald-100 rounded-2xl p-5 max-h-[500px] overflow-y-auto">
                                <div className="flex items-center justify-between mb-3">
                                    <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">
                                        ✅ Documento Funcional Corregido
                                    </p>
                                    <div className="flex gap-2">
                                        {[
                                            { label: "TXT", ext: "txt" },
                                            { label: "WORD", ext: "doc" },
                                            { label: "PDF", ext: "pdf" },
                                        ].map(({ label, ext }) => (
                                            <button
                                                key={ext}
                                                disabled={!!downloading}
                                                onClick={async () => {
                                                    setDownloading(ext);
                                                    await new Promise(r => setTimeout(r, 800));
                                                    if (ext === "txt") {
                                                        const blob = new Blob([fixedDoc], { type: "text/plain;charset=utf-8" });
                                                        const url = URL.createObjectURL(blob);
                                                        const a = document.createElement("a");
                                                        a.href = url;
                                                        a.download = "Especificacion_Funcional_Corregida.txt";
                                                        a.click();
                                                        URL.revokeObjectURL(url);
                                                    } else if (ext === "doc") {
                                                        const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word">
                  <head><meta charset="utf-8"><title>Especificación Funcional</title></head>
                  <body style="font-family:Calibri,sans-serif;font-size:11pt;line-height:1.5">
                    <pre style="font-family:Calibri,sans-serif;white-space:pre-wrap">${fixedDoc}</pre>
                  </body></html>`;
                                                        const blob = new Blob([html], { type: "application/msword;charset=utf-8" });
                                                        const url = URL.createObjectURL(blob);
                                                        const a = document.createElement("a");
                                                        a.href = url;
                                                        a.download = "Especificacion_Funcional_Corregida.doc";
                                                        a.click();
                                                        URL.revokeObjectURL(url);
                                                    } else if (ext === "pdf") {
                                                        const win = window.open("", "_blank");
                                                        win.document.write(`<html><head><title>Especificación Funcional</title>
                  <style>body{font-family:Calibri,sans-serif;font-size:11pt;line-height:1.6;padding:40px;white-space:pre-wrap}</style>
                  </head><body>${fixedDoc}</body></html>`);
                                                        win.document.close();
                                                        win.print();
                                                    }
                                                    setDownloading('');
                                                }}
                                                className={`text-[9px] font-black px-2.5 py-1 rounded-lg transition-all uppercase tracking-widest
              ${downloading === ext
                                                        ? 'bg-emerald-400 text-white cursor-wait'
                                                        : downloading
                                                            ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                                                            : 'bg-emerald-600 text-white hover:bg-emerald-700'
                                                    }`}
                                            >
                                                {downloading === ext ? (
                                                    <span className="flex items-center gap-1">
                                                        <span className="w-2.5 h-2.5 border border-white/40 border-t-white rounded-full animate-spin inline-block" />
                                                        Generando...
                                                    </span>
                                                ) : `⬇ ${label}`}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {downloading && (
                                    <div className="mb-3 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">
                                        <span className="w-3 h-3 border-2 border-emerald-300 border-t-emerald-600 rounded-full animate-spin shrink-0" />
                                        <p className="text-[10px] font-black text-emerald-600 uppercase tracking-wider">
                                            Preparando archivo {downloading.toUpperCase()} para descarga...
                                        </p>
                                    </div>
                                )}

                                <div className="space-y-2">
                                    {fixedDoc.split('\n').map((line, i) => {
                                        if (line.startsWith('## ')) return <h2 key={i} className="text-[13px] font-black text-slate-800 mt-4 mb-1 uppercase tracking-wide">{line.replace('## ', '')}</h2>;
                                        if (line.startsWith('# ')) return <h1 key={i} className="text-[15px] font-black text-slate-900 mt-4 mb-2 uppercase">{line.replace('# ', '')}</h1>;
                                        if (line.startsWith('- ') || line.startsWith('* ')) return (
                                            <div key={i} className="flex gap-2 text-[11px] text-slate-600">
                                                <span className="text-emerald-500 shrink-0">›</span>
                                                <span>{line.replace(/^[-*] /, '')}</span>
                                            </div>
                                        );
                                        if (line.trim() === '') return <div key={i} className="h-2" />;
                                        const parts = line.split(/\*\*(.*?)\*\*/g);
                                        return (
                                            <p key={i} className="text-[11px] text-slate-600 leading-relaxed">
                                                {parts.map((p, j) => j % 2 === 1
                                                    ? <strong key={j} className="text-slate-800 font-black">{p}</strong>
                                                    : p
                                                )}
                                            </p>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        {/* Secciones detalladas */}
                        <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
                            {SECTIONS_MODEL.map((model) => {
                                const sec = result.sections.find((s) => s.key === model.key);
                                const status = sec?.status ?? STATUS.missing;
                                const cfg = STATUS_CONFIG[status];

                                return (
                                    <div
                                        key={model.key}
                                        className={`p-4 rounded-2xl border-2 ${cfg.bg} ${cfg.border} transition-all`}
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex items-start gap-2 min-w-0">
                                                <span className="text-base shrink-0 mt-0.5">
                                                    {cfg.icon}
                                                </span>
                                                <div className="min-w-0">
                                                    <p
                                                        className={`text-[11px] font-black ${cfg.color} uppercase tracking-wide`}
                                                    >
                                                        {model.id}. {model.label}
                                                    </p>
                                                    {sec?.findings && (
                                                        <p className="text-[10px] text-slate-500 mt-1 leading-relaxed">
                                                            {sec.findings}
                                                        </p>
                                                    )}
                                                    {sec?.suggestions && status !== STATUS.complete && (
                                                        <p className="text-[10px] text-slate-400 mt-1 italic leading-relaxed">
                                                            💡 {sec.suggestions}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            {sec && (
                                                <span
                                                    className={`text-[10px] font-black shrink-0 ${cfg.color}`}
                                                >
                                                    {sec.score}/10
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default SpecValidatorWizard;
